import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { WorkspaceDataSource } from 'honeygrid-types'
import {
	CheckCircle,
	Clock,
	Eye,
	FileText,
	Loader2,
	MoreVertical,
	Trash2,
	XCircle,
} from 'lucide-react'

interface DataSourceCardProps {
	dataSource: WorkspaceDataSource
	onDelete: (id: string | number) => void
	onViewDetails: (id: string | number) => void
	selected?: boolean
	onClick?: () => void
}

const formatDate = (dateString: string): string => {
	return new Date(dateString).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
}

const getStatusBadge = (status: WorkspaceDataSource['status']) => {
	switch (status) {
		case 'ready':
			return (
				<div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
					<CheckCircle className="mr-1.5 h-3 w-3" />
					Ready
				</div>
			)
		case 'processing':
			return (
				<div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
					<Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
					Processing
				</div>
			)
		case 'pending':
			return (
				<div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
					<Clock className="mr-1.5 h-3 w-3" />
					Pending
				</div>
			)
		case 'failed':
			return (
				<div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
					<XCircle className="mr-1.5 h-3 w-3" />
					Failed
				</div>
			)
	}
}

const getTypeIcon = (type: WorkspaceDataSource['type']) => {
	switch (type) {
		case 'csv':
			return (
				<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-100 border border-emerald-200">
					<FileText className="h-4 w-4 text-emerald-600" />
				</div>
			)
		case 'google_ads':
			return (
				<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 border border-blue-200">
					<FileText className="h-4 w-4 text-blue-600" />
				</div>
			)
	}
}

const getTypeLabel = (type: WorkspaceDataSource['type']) => {
	switch (type) {
		case 'csv':
			return 'CSV Data Source'
		case 'google_ads':
			return 'Google Ads Data Source'
		default:
			return 'Data Source'
	}
}

export function DataSourceCard({ dataSource, onDelete, onViewDetails, selected = false, onClick }: DataSourceCardProps) {
	const { id, name, type, status, updated_at } = dataSource

	return (
		<Card className={`relative w-full max-w-md h-fit cursor-pointer transition-shadow hover:shadow-md ${selected ? 'ring-2 ring-primary' : ''}`} onClick={onClick}>
			<CardHeader className="relative">
				<div className="flex items-start justify-between">
					<div className="flex items-start space-x-3 min-w-0 flex-1">
						{getTypeIcon(type)}
						<div className="min-w-0 flex-1">
							<CardTitle className="text-lg font-semibold text-slate-900 truncate leading-tight">
								{name}
							</CardTitle>
							<p className="text-sm text-slate-500 mt-1">{getTypeLabel(type)}</p>
						</div>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
								<MoreVertical className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onClick={(e) => { e.stopPropagation(); onViewDetails(id); }}>
								<Eye className="h-4 w-4" />
								View Details
							</DropdownMenuItem>
							<DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(id); }} variant="destructive">
								<Trash2 className="h-4 w-4" />
								Delete
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</CardHeader>

			<CardContent className="relative pt-0 flex justify-between">
				{/* Status */}
				<div className="flex items-center justify-between">{getStatusBadge(status)}</div>

				{/* Footer */}
				{updated_at && (
					<div className="flex items-center space-x-2 text-sm">
						<Clock className="h-4 w-4" />
						<span>Updated {formatDate(updated_at)}</span>
					</div>
				)}
			</CardContent>
		</Card>
	)
}
